import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "grade-container" }
const _hoisted_2 = { class: "dashboard-header" }
const _hoisted_3 = { class: "header-left" }
const _hoisted_4 = { class: "exam-selector" }
const _hoisted_5 = { class: "header-right" }
const _hoisted_6 = { class: "countdown-card" }
const _hoisted_7 = { class: "countdown-info" }
const _hoisted_8 = { class: "number-group" }
const _hoisted_9 = { class: "number" }
const _hoisted_10 = { class: "main-content" }
const _hoisted_11 = { class: "left-column" }
const _hoisted_12 = { class: "content-card table-card" }
const _hoisted_13 = { class: "card-body" }
const _hoisted_14 = {
  key: 0,
  class: "content-card compare-card"
}
const _hoisted_15 = { class: "card-body" }
const _hoisted_16 = { class: "right-column" }
const _hoisted_17 = { class: "radar-chart-section card" }
const _hoisted_18 = { class: "card-body chart-container" }

import { getGradeApi } from '@/servers/api/grade';
import { getStudentExamApi } from '@/servers/api/student';
import { getPassLine } from '@/servers/api/analysis';
import { ref, onMounted, onUnmounted, onBeforeMount, computed } from 'vue';
import { useUserStore } from '@/store/modules/user';
import router from '@/router';
import { message } from 'ant-design-vue';
import CompareTable from './components/ExamAnalysis.vue';
import RadarChart from './components/Radar.vue';
import { getSubjectScore, getTotalScore, getSubjectClassRank, getSubjectSchoolRank, groupPassLine } from '@/utils/subjects';
import type { GradeDetail } from '@/utils/gradeTypes';

// 计算距离高考的天数
interface Exam {
    id:string,
    name:string
}

type TableData = {
    field: string;
    name: string;
    score: number;
    classRank: number;
    schoolRank: number;
    maxB: number;
    passLine: number | string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'Grade',
  setup(__props) {

const calculateDaysUntilExam = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    let examDate = new Date(currentYear, 5, 7);
    if (today > examDate) {
        examDate = new Date(currentYear + 1, 5, 7);
    }
    const diffTime = examDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
};

const currentExamData = ref<GradeDetail>();
const studentId = ref('');
const studentInfo = ref<any>(null);
const daysUntilExam = ref(calculateDaysUntilExam());
const userStore = useUserStore();

// 比较考试数据（初始化空数组，避免undefined）
const compareScoreData = ref<number[]>([]);
const updateCompareScoreData = (data: number[]) => {
    // 过滤异常大的数值（比如雷达图中的199、637）
    const validData = data.map(num => isFinite(num) && num <= 150 ? num : 0);
    compareScoreData.value = validData;
};

// 当前考试数据数组（过滤异常值）
const currentExamDataArray = computed(() => {
    let tmp: number[] = [];
    if(currentExamData.value) {
        for(let i of dynamicSubjectNames.value){
            const score = getSubjectScore(currentExamData.value, i.name);
            tmp.push(isFinite(score) && score <= 150 ? score : 0);
        }
    }
    return tmp;
});

// 根据选科动态确定科目名称
const dynamicSubjectNames = computed(() => {
    const base = [
        { name: '语文', max: 150 },
        { name: '英语', max: 150 },
        { name: '数学', max: 150 }
    ];
    const extras = [
        { short: '物', label: '物理' },
        { short: '化', label: '化学' },
        { short: '生', label: '生物' },
        { short: '史', label: '历史' },
        { short: '地', label: '地理' },
        { short: '政', label: '政治' }
    ] as const;
    const selection = studentInfo.value?.subject_selection;
    const hasSelection = typeof selection === 'string' && selection.length > 0;
    const result = [...base];
    for (const e of extras) {
        if (!hasSelection || selection.includes(e.short)) {
            result.push({ name: e.label, max: 100 });
        }
    }
    return result;
});

// 每天更新倒计时
let timer: number;
onMounted(() => {
    daysUntilExam.value = calculateDaysUntilExam();
    const updateAtMidnight = () => {
        const now = new Date();
        const night = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
        const msUntilMidnight = night.getTime() - now.getTime();
        timer = window.setTimeout(() => {
            daysUntilExam.value = calculateDaysUntilExam();
            updateAtMidnight();
        }, msUntilMidnight);
    };
    updateAtMidnight();
});

onUnmounted(() => {
    if (timer) clearTimeout(timer);
});

const examList = ref<Exam[]>([])
const currentExamId = ref<string>('')
const tableData = ref<TableData[]>([])
const passLineData = ref<Record<string, any>>({})

onBeforeMount(async () => {
    if (!userStore.isLogin) {
        router.push('/user/login');
        return
    }

    const userInfo = userStore.getUserInfo();
    studentId.value = userInfo.role
    studentInfo.value = userInfo.student
    try {
        const res = await getStudentExamApi({student_uid:userInfo.role},[]);
        if (res.code === 200 ) {
            // 1. 过滤有效考试数据
            const validExams: Exam[] = [];
            res.data.forEach((item: any) => {
                if (Array.isArray(item) && item[0]?.id && item[0]?.name) {
                    validExams.push({
                        id: item[0].id,
                        name: item[0].name
                    });
                }
            });
            // 2. 按ID从大到小排序（ID大=最后一次考试）
            examList.value = validExams.sort((a, b) => Number(b.id) - Number(a.id));
        }
        
        // 3. 默认选择第一个考试（最后一次）
        if(examList.value.length > 0){
            currentExamId.value = examList.value[0].id;
            const gradeRes = await getGradeApi({
                student_id: userInfo.role,
                exam_id: parseInt(currentExamId.value)
            });
            if (gradeRes.code === 200 && gradeRes.data?.[0]) {
                await fetchPassLine(currentExamId.value);
                handleGradeDetail(gradeRes.data[0]);
            }
        }else{
            message.error('似乎还没有参加一场考试？')
        }
    } catch (err) {
        message.error('加载考试数据失败');
    }
})

const fetchPassLine = async (examId: string) => {
    passLineData.value = {};
    try {
        const res = await getPassLine({ exam_id: parseInt(examId), size: 100 });
        if (res.code !== 200 || !Array.isArray(res.data)) return;
        // 行式数据按 line_group_id 分组，匹配含「特控/一段」的分数线组
        const grouped = new Map<number, any[]>();
        res.data.forEach((row: any) => {
            const gid = row?.line_group_id;
            if (gid == null) return;
            if (!grouped.has(gid)) grouped.set(gid, []);
            grouped.get(gid)!.push(row);
        });
        let targetGroup: any[] | null = null;
        for (const rows of grouped.values()) {
            const lineName = String(rows[0]?.line_name || '');
            if (lineName.includes('特控') || lineName.includes('一段')) {
                targetGroup = rows;
                break;
            }
        }
        if (!targetGroup) targetGroup = grouped.values().next().value || null;
        passLineData.value = targetGroup ? groupPassLine(targetGroup) : {};
    } catch (err) {
        passLineData.value = {};
    }
}

const getPassLineValue = (field: string) => {
    const value = passLineData.value?.[field];
    return value ?? '-';
}

const handleGradeDetail = (gradeData:GradeDetail) => {
    tableData.value = []
    currentExamData.value = gradeData
    // 过滤异常成绩（比如超过满分的数值）
    const safeScore = (score: any, max: number) => isFinite(score) && score <= max ? score : 0;

    tableData.value = [
    {
        field: '总分',
        name: '总分',
        score: safeScore(getTotalScore(gradeData), 750),
        classRank: getSubjectClassRank(gradeData, '总分') || 0,
        schoolRank: getSubjectSchoolRank(gradeData, '总分') || 0,
        maxB: safeScore(getTotalScore(gradeData), 750),
        passLine: getPassLineValue('总分'),
    },
    {
        field: '语文',
        name: '语文',
        score: safeScore(getSubjectScore(gradeData, '语文'), 150),
        classRank: getSubjectClassRank(gradeData, '语文') || 0,
        schoolRank: getSubjectSchoolRank(gradeData, '语文') || 0,
        maxB: safeScore(getSubjectScore(gradeData, '语文'), 150),
        passLine: getPassLineValue('语文'),
    },
    {
        field: '英语',
        name: '英语',
        score: safeScore(getSubjectScore(gradeData, '英语'), 150),
        classRank: getSubjectClassRank(gradeData, '英语') || 0,
        schoolRank: getSubjectSchoolRank(gradeData, '英语') || 0,
        maxB: safeScore(getSubjectScore(gradeData, '英语'), 150),
        passLine: getPassLineValue('英语'),
    },
    {
        field: '数学',
        name: '数学',
        score: safeScore(getSubjectScore(gradeData, '数学'), 150),
        classRank: getSubjectClassRank(gradeData, '数学') || 0,
        schoolRank: getSubjectSchoolRank(gradeData, '数学') || 0,
        maxB: safeScore(getSubjectScore(gradeData, '数学'), 150),
        passLine: getPassLineValue('数学'),
    }
];
    const selection = studentInfo.value?.subject_selection;
    const hasSelection = typeof selection === 'string' && selection.length > 0;
    const extras = [
        { short: '物', label: '物理' },
        { short: '化', label: '化学' },
        { short: '生', label: '生物' },
        { short: '史', label: '历史' },
        { short: '地', label: '地理' },
        { short: '政', label: '政治' }
    ] as const;
    for (const e of extras) {
        if (!hasSelection || selection.includes(e.short)) {
            const score = getSubjectScore(gradeData, e.label);
            tableData.value.push({
                field: e.label,
                name: e.label,
                score: safeScore(score, 100),
                classRank: getSubjectClassRank(gradeData, e.label) || 0,
                schoolRank: getSubjectSchoolRank(gradeData, e.label) || 0,
                maxB: safeScore(score, 100),
                passLine: getPassLineValue(e.label),
            });
        }
    }
}

const  tableColumns = [{
        title:'科目',
        dataIndex:'name',
        align: 'center',
    },{
        title: '成绩',
        dataIndex:'score',
        align: 'center',
    },{
        title: '班级排名',
        dataIndex:'classRank',
        align: 'center',
    },{
        title: '年级排名',
        dataIndex: 'schoolRank',
        align: 'center',
    },{
        title: '特控线',
        dataIndex:'passLine',
        align: 'center',
    }
]

const handleChange = async (value:string) => {
    currentExamId.value = value;
    try {
        const gradeRes = await getGradeApi({
            student_id: parseInt(studentId.value),
            exam_id: parseInt(value)
        });
        if (gradeRes.code === 200 && gradeRes.data?.[0]) {
            await fetchPassLine(value);
            handleGradeDetail(gradeRes.data[0]);
        }
    } catch (err) {
        message.error('切换考试失败');
    }
}

return (_ctx: any,_cache: any) => {
  const _component_a_select_option = _resolveComponent("a-select-option")!
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_table = _resolveComponent("a-table")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[2] || (_cache[2] = _createElementVNode("h1", { class: "page-title" }, "成绩分析看板", -1)),
        _createElementVNode("div", _hoisted_4, [
          _cache[1] || (_cache[1] = _createElementVNode("span", { class: "label" }, "当前考试：", -1)),
          _createVNode(_component_a_select, {
            placeholder: "请选择考试",
            style: {"width":"240px"},
            onChange: handleChange,
            value: currentExamId.value,
            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((currentExamId).value = $event)),
            class: "custom-select",
            bordered: false
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(examList.value, (item) => {
                return (_openBlock(), _createBlock(_component_a_select_option, {
                  key: item.id,
                  value: item.id
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(item.name), 1)
                  ]),
                  _: 2
                }, 1032, ["value"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["value"])
        ])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          _cache[5] || (_cache[5] = _createElementVNode("div", { class: "icon-wrapper" }, [
            _createElementVNode("span", {
              role: "img",
              "aria-label": "hourglass"
            }, "⏳")
          ], -1)),
          _createElementVNode("div", _hoisted_7, [
            _cache[4] || (_cache[4] = _createElementVNode("span", { class: "label" }, "距离高考仅剩", -1)),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("span", _hoisted_9, _toDisplayString(daysUntilExam.value), 1),
              _cache[3] || (_cache[3] = _createElementVNode("span", { class: "unit" }, "天", -1))
            ])
          ])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("div", _hoisted_12, [
          _cache[6] || (_cache[6] = _createElementVNode("div", { class: "card-header" }, [
            _createElementVNode("div", { class: "header-title" }, [
              _createElementVNode("span", { class: "indicator" }),
              _createElementVNode("h3", null, "成绩概览")
            ]),
            _createElementVNode("span", { class: "subtitle" }, "详细得分情况")
          ], -1)),
          _createElementVNode("div", _hoisted_13, [
            _createVNode(_component_a_table, {
              columns: tableColumns,
              "data-source": tableData.value,
              pagination: false,
              class: "custom-table",
              size: "middle",
              rowKey: "name"
            }, null, 8, ["data-source"])
          ])
        ]),
        (examList.value.length > 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _cache[7] || (_cache[7] = _createElementVNode("div", { class: "card-header" }, [
                _createElementVNode("div", { class: "header-title" }, [
                  _createElementVNode("span", { class: "indicator green" }),
                  _createElementVNode("h3", null, "进退步分析")
                ])
              ], -1)),
              _createElementVNode("div", _hoisted_15, [
                _createVNode(CompareTable, {
                  student_id: parseInt(studentId.value),
                  selected_id: parseInt(currentExamId.value),
                  "exam-list": examList.value.slice(1),
                  "student-info": studentInfo.value,
                  subjects: dynamicSubjectNames.value,
                  "onUpdate:compareScoreData": updateCompareScoreData
                }, null, 8, ["student_id", "selected_id", "exam-list", "student-info", "subjects"])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_16, [
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createVNode(RadarChart, {
              "student-info": studentInfo.value,
              "compare-score-data": compareScoreData.value,
              "current-exam-data": currentExamDataArray.value,
              subjects: dynamicSubjectNames.value
            }, null, 8, ["student-info", "compare-score-data", "current-exam-data", "subjects"])
          ])
        ])
      ])
    ])
  ]))
}
}

})